/**
 * A round trip, in the three words a player can act on.
 *
 * A band rather than the millisecond figure it came from, and that is the whole point. A number
 * invites comparing two players in a room, and there is nothing either of them can do about the
 * answer; a band says whether this connection is going to hold up an evening, which is the one
 * question being asked. The figure stays on the host, where the smoothing happens, and only the
 * band crosses the wire.
 *
 * The thresholds are picked off the play head, not off a table of what feels fast on a website. A
 * guest draws the world `INTERPOLATION_DELAY_MS` behind the host and predicts its own body
 * forward, so a round trip inside that window is hidden by machinery that already exists, one about
 * twice it is felt as a correction now and again, and one past that is felt as the game arguing.
 */

/** How a link reads once it has been measured. `unmeasured` is the honest answer until then. */
export type LatencyBand = 'unmeasured' | 'good' | 'fair' | 'poor';

/** Inside the play head: prediction and interpolation cover the whole trip. */
export const GOOD_UNDER_MS = 100;

/** Past the play head but inside a correction a smoother can absorb. */
export const FAIR_UNDER_MS = 200;

const LABELS: Record<LatencyBand, string> = {
  unmeasured: '',
  good: 'fast',
  fair: 'fair',
  poor: 'slow',
};

/** Null is a link nothing has come back from yet, which is not the same as a slow one. */
export function bandOf(rttMs: number | null): LatencyBand {
  if (rttMs === null) return 'unmeasured';
  if (rttMs < GOOD_UNDER_MS) return 'good';
  if (rttMs < FAIR_UNDER_MS) return 'fair';
  return 'poor';
}

/**
 * What a player list renders. The only place these words are written.
 *
 * `unmeasured` has no word, because a chip saying so would be a row telling somebody that nothing
 * is known yet, once a second, until something is. Whoever renders this leaves the chip off.
 */
export function bandLabel(band: LatencyBand): string {
  return LABELS[band];
}
